import React from "react";
import { ChartName } from "./analyticsTypes";

export const STORAGE_KEYS = {
  LAYOUTS: "rpchartlayouts",
  ACTIVE_CHARTS: "rpactivecharts",
} as const;

const DEFAULT_ACTIVE_CHARTS: ChartName[] = [
  "referralConversions",
  "referralfees",
  "growthHistory",
  "coinsVisited",
  "referralHoldings",
  "tradinghistory",
  "depositVolume",
  "timeToFirstTrade",
  "assetsTraded",
];

/**
 * Custom hook to manage active charts state
 */
export const useActiveCharts = () => {
  /**
   * State management for active charts using localStorage persistence.
   * Initializes with saved charts from localStorage or falls back to defaults.
   *
   * @returns Array of active chart Names
   */
  const [activeCharts, setActiveCharts] = React.useState<ChartName[]>(() => {
    try {
      const savedCharts = localStorage.getItem(STORAGE_KEYS.ACTIVE_CHARTS);
      return savedCharts ? JSON.parse(savedCharts) : DEFAULT_ACTIVE_CHARTS;
    } catch (error) {
      console.error("Error loading active charts:", error);
      return DEFAULT_ACTIVE_CHARTS;
    }
  });

  /**
   * Updates the active charts state and persists changes to localStorage.
   * This function handles both state updates and storage synchronization
   * to ensure consistency between app state and stored preferences.
   *
   * @param newCharts - Array of chart IDs to set as active
   */
  const updateActiveCharts = React.useCallback((newCharts: ChartName[]) => {
    setActiveCharts(newCharts);
    localStorage.setItem(STORAGE_KEYS.ACTIVE_CHARTS, JSON.stringify(newCharts));
  }, []);

  /**
   * Adds a new chart to the active charts list.
   * Preserves existing active charts and appends the new chart.
   * Persists the updated list to localStorage via updateActiveCharts.
   *
   * @param chartType - Chart name of the chart to add
   */
  const addChart = React.useCallback(
    (chartType: ChartName) => {
      updateActiveCharts([...activeCharts, chartType]);
    },
    [activeCharts, updateActiveCharts],
  );

  /**
   * Removes a chart from the active charts list.
   * Filters out the specified chart while preserving all others.
   * Persists the updated list to localStorage via updateActiveCharts.
   *
   * @param chartType - Chart name of the chart to remove
   */
  const removeChart = React.useCallback(
    (chartType: ChartName) => {
      updateActiveCharts(activeCharts.filter((type) => type !== chartType));
    },
    [activeCharts, updateActiveCharts],
  );

  return {
    DEFAULT_ACTIVE_CHARTS,
    activeCharts,
    addChart,
    removeChart,
  };
};
